<template>
  <v-content>
    <div id="recaptcha-container" />

    <connect-account-dialog
      :opened.sync="connectAccountDialogOpened"
      @linkAccount="linkAccount"
    />

    <v-container justify-center class="settings__container">
      <v-layout class="mb-2" align-center>
        <soraban-action-button
          @click="$router.push('/overview')"
          icon="keyboard_arrow_left"
          help="Back"
        />
        <h1>Profile</h1>
        <v-spacer />
      </v-layout>
      <v-card flat class="content-container">
        <v-card-text>
          <v-tabs
            v-model="active"
            color="white"
            slider-color="primary"
            class="mb-4"
          >
            <v-tab key="settings" ripple class="text-none subheading">
              Settings
            </v-tab>
            <v-tab
              key="email"
              ripple
              class="text-none subheading"
              ref="integrationsTab"
            >
              Integrations
            </v-tab>
          </v-tabs>

          <template v-if="active === 0">
            <v-layout align-center justify-center>
              <div />
              <div>
                <form ref="$profilePicForm" @submit.prevent>
                  <input
                    type="file"
                    style="display: none"
                    ref="selectedImg"
                    @change="onFileChange"
                    name="profile"
                    accept="image/*"
                  />
                </form>
                <v-layout column align-center>
                  <v-badge overlap>
                    <template v-slot:badge>
                      <v-icon dark @click="uploadProfilePic()">edit</v-icon>
                    </template>

                    <v-avatar class="primary">
                      <img
                        v-if="currentUser.thumb_url"
                        :src="currentUser.thumb_url"
                        alt="Profile pic"
                      />
                      <v-icon v-else color="white"> person </v-icon>
                    </v-avatar>
                  </v-badge>
                </v-layout>
              </div>
              <div class="ml-2">
                <v-layout align-center>
                  {{ currentUser.full_name }}
                </v-layout>
              </div>
            </v-layout>
            <v-layout class="mb-2">
              <v-spacer />
              <span class="caption error--text">*Required</span>
            </v-layout>
            <v-layout class="text-xs-left" wrap>
              <v-flex xs12 sm6 class="px-2">
                <div>First Name <span class="error--text">*</span></div>
                <v-text-field
                  name="first-name"
                  placeholder="Jack"
                  type="text"
                  v-model="currentUser.fname"
                  required
                  :rules="fieldRules"
                  solo
                  class="default-input ph-no-capture"
                  id="first-name"
                />
              </v-flex>
              <v-flex xs12 sm6 class="px-2">
                <div>Last Name <span class="error--text">*</span></div>
                <v-text-field
                  name="last-name"
                  placeholder="Smith"
                  type="text"
                  v-model="currentUser.lname"
                  required
                  :rules="fieldRules"
                  solo
                  class="default-input ph-no-capture"
                  id="last-name"
                />
              </v-flex>
            </v-layout>
            <v-layout justify-center align-center class="text-xs-left" wrap>
              <v-flex xs12 sm6 class="px-2">
                <v-layout>
                  Primary Contact Email
                  <v-tooltip bottom>
                    <v-icon slot="activator" size="15" class="ml-1">
                      information
                    </v-icon>
                    <span
                      >Please contact one of your admins if you wish to change
                      your email.</span
                    >
                  </v-tooltip>
                </v-layout>
                <v-text-field
                  disabled
                  solo
                  label="Email"
                  v-model="currentUser.email"
                  class="default-input ph-no-capture"
                />
              </v-flex>

              <v-flex xs12 sm6 class="px-2">
                <div>Phone</div>
                <v-text-field
                  solo
                  v-model="currentUser.phone_number"
                  class="default-input ph-no-capture"
                  label="(777) 777- 7777"
                  :rules="[]"
                  mask="phone"
                />
              </v-flex>
            </v-layout>
            <v-layout class="mb-4">
              <v-flex xs12 sm6 class="px-2 text-xs-left">
                <div>Time Zone</div>
                <v-select
                  v-model="currentUser.time_zone"
                  solo
                  hide-details
                  :items="timeZones"
                  class="default-input"
                />
              </v-flex>
            </v-layout>
            <v-layout class="mb-4">
              <v-flex xs6 sm6 class="px-2 text-xs-left">
                <div>Two-Factor Authentication</div>
                <v-switch
                  class="default-input ma-0 pa-0"
                  v-model="currentUser.enabled_2fa"
                  :disabled="force2FAOnUsers || force2FAOnFirmUsers"
                  :true-value="true"
                  :false-value="false"
                  label="2FA"
                />
                <div class="profile-normalize-switch-margin ph-no-capture">
                  <sx-tiny color="gray">
                    When you activate Two-Factor Authentication, you will
                    receive a login link via email ({{ currentUser.email }})
                    prior to each login attempt.
                  </sx-tiny>
                </div>
              </v-flex>
            </v-layout>

            <v-layout
              v-if="currentUser.notification_setting"
              class="text-xs-left mb-4"
              wrap
            >
              <v-flex xs12 sm6 class="px-2 mb-4">
                <div>Document Related Notifications</div>
                <v-select
                  v-model="currentUser.notification_setting.document_related"
                  solo
                  hide-details
                  item-text="name"
                  item-value="value"
                  :items="notificationMethods"
                  class="default-input"
                />
              </v-flex>
              <v-flex xs12 sm6 class="px-2 text-xs-left mb-4">
                <div>Questionnaire Related Notifications</div>
                <v-select
                  v-model="
                    currentUser.notification_setting.questionnaire_related
                  "
                  solo
                  hide-details
                  item-text="name"
                  item-value="value"
                  :items="notificationMethods"
                  class="default-input"
                />
              </v-flex>
              <v-flex xs12 sm6 class="px-2 text-xs-left mb-4">
                <div>Detail Change Notifications</div>
                <v-select
                  v-model="currentUser.notification_setting.detail_change"
                  solo
                  hide-details
                  item-text="name"
                  item-value="value"
                  :items="notificationMethods"
                  class="default-input"
                />
              </v-flex>
              <v-flex
                xs12
                sm6
                class="px-2 mb-4"
                v-if="!isFirmCommunicationDisabled"
              >
                <div>Conversation Related Notifications</div>
                <v-select
                  v-model="
                    currentUser.notification_setting.conversation_related
                  "
                  solo
                  hide-details
                  item-text="name"
                  item-value="value"
                  :items="conversationNotificationMethods"
                  class="default-input"
                />
              </v-flex>
              <v-flex xs12 sm6 class="px-2 text-xs-left mb-4">
                <div>Receive Empty Notification Summary?</div>
                <v-switch
                  :label="
                    currentUser.notification_setting
                      .enable_blank_notification_summary
                      ? 'Yes'
                      : 'No'
                  "
                  v-model="
                    currentUser.notification_setting
                      .enable_blank_notification_summary
                  "
                  hide-details
                  class="default-input mt-0 pt-0"
                />
              </v-flex>
            </v-layout>

            <v-layout
              justify-start
              align-center
              class="text-xs-left mb-4"
              wrap
              v-if="currentUser.out_of_office_setting"
            >
              <v-flex v-if="!isFirmCommunicationDisabled" xs6 class="px-2">
                <div class="text-xs-left">Out of Office</div>
                <v-switch
                  :label="
                    currentUser.out_of_office_setting.enabled ? 'Yes' : 'No'
                  "
                  v-model="currentUser.out_of_office_setting.enabled"
                  hide-details
                  class="default-input mt-0 pt-0"
                />
              </v-flex>
              <v-flex
                xs6
                class="px-2"
                v-if="currentUser.out_of_office_setting.enabled"
              >
                <div>
                  Redirect Notifications to
                  <v-tooltip bottom>
                    <v-icon slot="activator" size="15"> information </v-icon>
                    <span
                      >This user will also receive all your notifications and
                      messages while you are gone</span
                    >
                  </v-tooltip>
                </div>
                <v-select
                  v-model="currentUser.out_of_office_setting.redirect_user_id"
                  solo
                  hide-details
                  item-text="name"
                  placeholder="Choose a team member"
                  item-value="id"
                  :items="firmUsers"
                  class="default-input"
                />
              </v-flex>
            </v-layout>

            <v-layout
              wrap
              v-if="
                currentUser.out_of_office_setting &&
                  currentUser.out_of_office_setting.enabled
              "
            >
              <v-flex xs12 class="px-2">
                <div class="text-xs-left">
                  Default Message
                  <v-tooltip bottom>
                    <v-icon slot="activator" size="15"> information </v-icon>
                    <span
                      >Your client will be able to see this message on top of
                      the conversation</span
                    >
                  </v-tooltip>
                </div>
                <v-textarea
                  :disabled="!currentUser.out_of_office_setting.enabled"
                  solo
                  placeholder="Hello, I am temporarily out of office. I will get back to you when I am back in office."
                  v-model="currentUser.out_of_office_setting.default_message"
                  class="default-input"
                />
              </v-flex>
            </v-layout>

            <v-layout align-center class="text-xs-center">
              <v-spacer class="hidden-xs-only" />
              <v-tooltip bottom>
                <v-btn
                  slot="activator"
                  outline
                  class="primary--text text-capitalize mr-2"
                  @click="sendDailyNotification"
                >
                  Send Notification Summary
                </v-btn>
                <span
                  >You will receive an email with a summary of all the
                  notifications you received today.</span
                >
              </v-tooltip>
              <v-btn
                outline
                class="primary--text text-capitalize mx-2"
                id="reset-password-button"
                @click="resetPassword"
              >
                Reset Password
              </v-btn>
              <v-btn
                id="save-changes-button"
                class="primary text-capitalize ml-2"
                @click="save"
                :disabled="!userSavable"
              >
                <v-icon> check </v-icon>
                Save Changes
              </v-btn>
            </v-layout>
          </template>

          <v-layout column class="px-2" v-else-if="active === 1">
            <v-layout align-center justify-space-between>
              <div class="title">Email Integration</div>
              <v-btn
                class="primary text-none font-weight-bold"
                id="connect-account-btn"
                @click="connectAccountDialogOpened = true"
                >Connect account</v-btn
              >
            </v-layout>

            <v-data-table
              :headers="linkedAccountHeaders"
              :items="linkedAccounts"
              :loading="linkedAccountsTableLoading"
              hide-actions
              class="text-xs-left"
            >
              <template slot="headerCell" slot-scope="props">
                <template>
                  <span>
                    {{ props.header.text }}
                  </span>
                </template>
              </template>

              <template slot="items" slot-scope="props">
                <tr :key="props.index" slot="header">
                  <td class="text-xs-left">
                    {{ props.item.email }}
                  </td>

                  <td class="text-xs-left">
                    <img
                      v-if="props.item.provider == 'google'"
                      class="provider-logo"
                      :src="require('@/assets/google.png')"
                    />
                    <img
                      v-else-if="props.item.provider == 'microsoft'"
                      class="provider-logo"
                      :src="require('@/assets/microsoft.png')"
                    />
                  </td>

                  <td class="text-xs-left">
                    <span class="red--text" v-if="props.item.expired_at"
                      >Inactive</span
                    >
                    <span class="green--text" v-else>Active</span>
                  </td>

                  <td clas="text-center">
                    <v-layout align-center>
                      <v-menu
                        transition="slide-y-transition"
                        bottom
                        nudge-bottom="30"
                        class="my-1"
                      >
                        <v-icon slot="activator"> more_vert </v-icon>
                        <v-list>
                          <v-list-tile
                            @click="
                              unlinkAccount(
                                props.item.email,
                                props.item.provider,
                              )
                            "
                          >
                            <v-list-tile-title> Remove </v-list-tile-title>
                          </v-list-tile>
                        </v-list>
                      </v-menu>
                    </v-layout>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-container>
  </v-content>
</template>

<script>
import { ref, reactive, computed, onMounted } from '@vue/composition-api';
import { useStore } from 'vuex-composition-helpers';
import firebase from 'firebase/app';
import 'firebase/auth';
import API from '@/api';
import { states, timeZones } from '@/constants/variables';
import ConnectAccountDialog from '@/components/dialogs/ConnectAccountDialog';
import has from 'lodash/has';
import SxTiny from '@/components/sx/SxTiny';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';

export default {
  components: {
    ConnectAccountDialog,
    SxTiny,
  },

  mixins: [FirmPreferenceMixins],

  setup(_, { root }) {
    const store = useStore();

    let recaptchaVerifier = ref(null);
    let selectedImg = ref(null);
    let myAvailabilityDialogOpened = ref(false);
    let selectedDayName = ref('Monday');
    let savedAvailability = ref([]);
    let $profilePicForm = ref(null);
    const fieldRules = ref([v => !!v || 'This field is required']);
    let firmUsers = ref([]);
    const phoneRules = ref([
      v => !!v || 'Required for Two-factor authentication',
    ]);

    const notificationMethods = ref([
      {
        name: 'Email Immediately',
        value: 'email',
      },
      {
        name: 'Daily Summary',
        value: 'daily_batch',
      },
      {
        name: 'No Email',
        value: 'none',
      },
    ]);
    const conversationNotificationMethods = ref([
      {
        name: 'Email Immediately',
        value: 'email',
      },
      {
        name: 'No Email',
        value: 'none',
      },
    ]);
    const userSavable = computed(() => {
      return currentUser.value.fname && currentUser.value.lname;
    });

    const currentUser = computed(() => store.state.auth.user);
    const isAdvisor = computed(() => store.getters.isAdvisor);
    const isFirmDocumentsDisabled = computed(
      () => store.getters.isFirmCommunicationDisabled,
    );
    const isFirmCommunicationDisabled = computed(
      () => store.getters.isFirmCommunicationDisabled,
    );

    const active = ref(0);

    async function save() {
      store.commit('application/START_LOADING');
      const {
        id,
        fname,
        lname,
        phone_number,
        time_zone,
        out_of_office_setting,
        enabled_2fa,
        notification_setting,
      } = currentUser.value || {};

      const { enabled, redirect_user_id, default_message } =
        out_of_office_setting || {};

      const {
        questionnaire_related,
        document_related,
        conversation_related,
        detail_change,
        enable_blank_notification_summary,
      } = notification_setting || {};

      try {
        await API.advisor.updateAdvisor({
          id,
          fname,
          lname,
          phone_number,
          time_zone,
          enabled,
          redirect_user_id,
          default_message,
          enabled_2fa,
          questionnaire_related,
          document_related,
          conversation_related,
          detail_change,
          enable_blank_notification_summary,
        });
        await store.dispatch('getCurrentUser');
        store.dispatch('application/setSuccessNotification', {
          message: 'The information is updated successfully.',
        });
      } catch (e) {
        store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong while saving information.',
          errorToSend: e,
          userEmail: currentUser.email,
        });
      } finally {
        store.commit('application/END_LOADING');
      }
    }

    async function resetPassword() {
      const auth = await firebase.auth();
      const email = currentUser.value.email;

      try {
        await auth.sendPasswordResetEmail(email);
        store.dispatch('application/setSuccessNotification', {
          message: `Reset email sent to ${email}`,
        });
      } catch (e) {
        console.log(e);
      }
    }

    async function sendDailyNotification() {
      try {
        await API.advisor.sendDailyNotification();
        store.dispatch('application/setSuccessNotification', {
          message: `Daily notification sent`,
        });
      } catch (e) {
        console.log(e);
      }
    }

    const uploadProfilePic = () => {
      selectedImg.value.type = 'text';
      selectedImg.value.type = 'file';
      selectedImg.value.click();
    };

    const onFileChange = async e => {
      try {
        store.commit('application/START_LOADING');
        const picture = e.target.files[0];
        let formData = new FormData($profilePicForm.value);
        formData.append('image', picture);

        await API.user.updateProfilePic(formData);
        await store.dispatch('getCurrentUser');
      } catch (e) {
        store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong while saving information.',
          errorToSend: e,
        });
      } finally {
        store.commit('application/END_LOADING');
      }
    };

    const connectAccountDialogOpened = ref(false);
    const linkedAccounts = ref([]);
    const linkedAccountsTableLoading = ref(true);
    const linkedAccountHeaders = ref([
      {
        text: 'Email',
        align: 'left',
        sortable: true,
        value: 'email',
        width: '50%',
      },
      {
        text: 'Provider',
        align: 'left',
        sortable: true,
        value: 'provider',
        width: '35%',
      },
      {
        text: 'Status',
        align: 'left',
        sortable: true,
        value: 'status',
        width: '10%',
      },
      {
        text: 'Actions',
        align: 'center',
        sortable: false,
        value: 'source',
        width: '5%',
      },
    ]);

    async function getLinkedAccounts() {
      linkedAccountsTableLoading.value = true;
      const response = await API.user.getLinkedAccounts();
      linkedAccounts.value = response.data.accounts;

      linkedAccountsTableLoading.value = false;
    }

    async function linkAccount(provider, code) {
      try {
        linkedAccountsTableLoading.value = true;
        await API.user.linkAccount({
          provider,
          code,
          // required by microsoft to get access token
          redirectUri: window.location.origin + '/admin-profile',
        });
        await getLinkedAccounts();
      } catch (error) {
        console.log(error);
      } finally {
        linkedAccountsTableLoading.value = false;
      }
    }

    async function unlinkAccount(email, provider) {
      try {
        linkedAccountsTableLoading.value = true;
        await API.user.unlinkAccount({ email, provider });
        await getLinkedAccounts();
      } catch (error) {
        console.log(error);
      } finally {
        linkedAccountsTableLoading.value = false;
      }
    }

    // Mounted
    onMounted(async () => {
      console.log(`the component is now mounted.`);

      recaptchaVerifier.value = new firebase.auth.RecaptchaVerifier(
        'recaptcha-container',
        {
          size: 'invisible',
        },
      );

      if (has(root.$route.query, 'integrations')) {
        active.value = 1;
      }

      try {
        const response = await API.firm.getAllUsers();

        if (response.data.error) {
          console.log(response.data.error);
        } else {
          firmUsers = [
            {
              id: null,
              name: 'None',
            },
            ...response.data.users.filter(u => u.id !== currentUser.id),
          ];
        }
      } catch (e) {
        console.log(e);
      } finally {
        store.commit('application/END_LOADING');
      }

      await getLinkedAccounts();
    });

    return {
      states,
      timeZones,
      recaptchaVerifier,
      notificationMethods,
      conversationNotificationMethods,
      fieldRules,
      firmUsers,
      phoneRules,
      currentUser,
      isAdvisor,
      isFirmCommunicationDisabled,
      isFirmDocumentsDisabled,
      resetPassword,
      uploadProfilePic,
      onFileChange,
      userSavable,
      save,
      selectedImg,
      $profilePicForm,
      active,
      connectAccountDialogOpened,
      linkedAccounts,
      linkedAccountsTableLoading,
      linkedAccountHeaders,
      getLinkedAccounts,
      unlinkAccount,
      linkAccount,
      sendDailyNotification,
    };
  },
};
</script>

<style lang="postcss" scoped>
.container {
  max-width: 1000px;
}
.settings__container {
  max-width: 700px;
}
.google-sign-in {
  cursor: pointer;
}
.title {
  font-weight: 700;
}
.provider-logo {
  width: 24px;
  height: 24px;
}

/* NOTE @chris A hack, but safer than replacing switch or rewrite it globally. */
.profile-normalize-switch-margin {
  margin-top: -16px;
}
</style>
